import { FC, Fragment, useMemo } from "react";
import { Grid, useMediaQuery } from "@mui/material";
import { toBigInt, WeiPerEther, ZeroAddress } from "ethers";
import { useParams } from "react-router";

import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { StyledEmptyWrapper, StyledPagination } from "@framework/styled";
import type { IMysteryBox } from "@framework/types";
import { useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";

import { MysteryBoxListItem } from "./item";
import { MysteryBoxSearchForm } from "./search";

export interface IMysteryboxListProps {
  embedded?: boolean;
}

export const MysteryBoxList: FC<IMysteryboxListProps> = props => {
  const { embedded } = props;

  const { id } = useParams<{ id: string }>();

  const initialSearchValues = useMemo(() => {
    return {
      contractIds: embedded ? [~~id!] : [],
      minPrice: ZeroAddress.toString(),
      maxPrice: (WeiPerEther * toBigInt(1000)).toString(),
    };
  }, [id]);

  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount } = useFetchQuery<
    IPaginationResult<IMysteryBox>
  >("/mystery/boxes", { enableInfiniteScroll: isMd }, void 0, initialSearchValues);

  return (
    <Fragment>
      <Breadcrumbs path={["dashboard", "mystery", "mystery.boxes"]} isHidden={embedded} />

      <PageHeader message="pages.mystery.boxes.title" />

      <MysteryBoxSearchForm initialValues={initialSearchValues} />

      <ProgressOverlay isLoading={isLoading}>
        <Grid container spacing={2}>
          <StyledEmptyWrapper count={allRows.length} isLoading={isLoading}>
            {allRows.map(mysterybox => (
              <Grid size={{ lg: 4, sm: 6, xs: 12 }} key={mysterybox.id}>
                <MysteryBoxListItem mysteryBox={mysterybox} />
              </Grid>
            ))}
          </StyledEmptyWrapper>
        </Grid>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}
    </Fragment>
  );
};
